import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import CheckoutAdditionalService from '../..';
import { ThemeProvider, useTheme } from '../../../theme';
import { getFfpBonusesForFlightByTravellers, getIsFFPUsed, getMilesForOrder, getOrderId, getTravellers } from '../../../Checkout/store/order/selectors';
import { useConfig } from '../../../context';
import { TravellerFieldEnum, useAddTravellersDataMutation } from '@websky/graphql';
import { getTravellerName, getUserValue } from '../../../utils';
import { useConfigRedirects } from '../../../hooks';
import LinkCardsModal from '../../../Loyalty/components/LinkCardsModal/LinkCardsModal';
import LoyaltyConnect from '../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import { getUserInfo } from '../../../Checkout/utils';
import { LoyaltyCardsType } from '../../../Loyalty/types';
import { formatError, getCorporateNumber } from '../../../Loyalty/utils';
import { Log } from '../../../sentry';
import LoyaltySummary from '../../../ServiceSummary/services/LoyaltySummary';
import { useRenderers } from '../../../renderProps';
import { fillOrder } from '../../../Checkout/store/order/actions';
import { Card as CardIcon, TransparentStar as TransparentStarIcon } from '../../../Icons';
import CdnImage from '../../../CdnImage';
const Loyalty = ({ userInfo, refetchUser, isServiceV2 }) => {
    const { t } = useTranslation('Checkout');
    const { LoyaltyV2: css } = useTheme('CheckoutAdditionalService');
    const orderId = useSelector(getOrderId);
    const travellers = useSelector(getTravellers);
    const travellersWithAvailableLoyalty = travellers.filter(traveller => traveller.values.some(value => value.type === TravellerFieldEnum.LoyaltyNumber));
    const isFFPProvidedToOrder = useSelector(getIsFFPUsed);
    const milesBonus = useSelector(getMilesForOrder);
    const ffpBonusesForFlightByTravellers = useSelector(getFfpBonusesForFlightByTravellers);
    const allTravellersFillFFP = travellersWithAvailableLoyalty.every(traveller => getUserValue(traveller, TravellerFieldEnum.LoyaltyNumber));
    const dispatch = useDispatch();
    const { global: { companyInfo: { loyaltyProgramAccountUrl, loyaltyProgramName } } } = useConfig();
    const [isLoyaltyModalOpen, setLoyaltyModalOpen] = useState(false);
    const [isConnectModalOpen, setConnectModalOpen] = useState(false);
    const [formError, setFormError] = React.useState('');
    const clearFormError = () => setFormError('');
    const ffpAuthState = useMemo(() => {
        if (!userInfo) {
            return 'unauthorized';
        }
        if (userInfo.isFfpAuthorizationNeeded) {
            return 'auth_without_ffp';
        }
        if (!userInfo.isFfpAuthorizationNeeded) {
            return 'auth_with_ffp';
        }
        return 'unauthorized';
    }, [userInfo]);
    const isActive = true;
    const { onGoToAccountSettings } = useConfigRedirects();
    const [setTravellersData, { loading }] = useAddTravellersDataMutation();
    const openLoyaltyModal = () => setLoyaltyModalOpen(true);
    const closeLoyaltyModal = () => setLoyaltyModalOpen(false);
    const openConnectModal = () => setConnectModalOpen(true);
    const closeConnectModal = () => setConnectModalOpen(false);
    const handleViewStatusClick = useCallback(() => {
        if (loyaltyProgramAccountUrl) {
            window.open(loyaltyProgramAccountUrl, '_blank');
        }
        else {
            Log({ level: 'error', message: 'loyaltyProgramAccountUrl is not set' });
            return null;
        }
    }, [isFFPProvidedToOrder]);
    const buttons = useMemo(() => {
        if (!allTravellersFillFFP) {
            return [
                {
                    text: t('Add ffp cards'),
                    action: () => setLoyaltyModalOpen(true),
                    variant: 'optionAction'
                }
            ];
        }
        return [
            {
                text: t('My miles'),
                action: handleViewStatusClick,
                variant: 'optionAction'
            }
        ];
    }, [allTravellersFillFFP, handleViewStatusClick]);
    const onLoyaltyCardSaved = () => {
        closeLoyaltyModal();
        closeConnectModal();
        refetchUser === null || refetchUser === void 0 ? void 0 : refetchUser();
    };
    const user = React.useMemo(() => getUserInfo(userInfo), [userInfo]);
    const onSubmit = ({ travellers: travellerFields }) => __awaiter(void 0, void 0, void 0, function* () {
        const updateTravellers = [];
        Object.entries(travellerFields).forEach(([id, travellerFields]) => {
            const relatedOrderTraveller = travellers.find(orderTraveller => orderTraveller.id === id);
            if (getUserValue(relatedOrderTraveller, TravellerFieldEnum.LoyaltyNumber) !== '') {
                return;
            }
            if (!travellerFields.loyaltyCardNumber) {
                return;
            }
            const values = [];
            values.push({
                type: TravellerFieldEnum.LoyaltyNumber,
                value: travellerFields.loyaltyCardNumber
            });
            if (travellerFields.loyaltyCardType === LoyaltyCardsType.CorporateCard) {
                values[0].value = getCorporateNumber(travellerFields.loyaltyCardNumber);
            }
            updateTravellers.push({
                id,
                values
            });
        });
        if (!updateTravellers.length) {
            onLoyaltyCardSaved();
            return;
        }
        try {
            const { data } = yield setTravellersData({
                variables: {
                    variables: {
                        orderId,
                        travellersData: updateTravellers
                    }
                }
            });
            if (!!data.SetTravellersData) {
                onLoyaltyCardSaved();
                dispatch(fillOrder(data.SetTravellersData));
            }
        }
        catch (error) {
            setFormError(t(formatError(error)));
        }
    });
    const serviceSummaryOptionStyles = useMemo(() => ({
        ServiceSummary: {
            ServiceSummaryOption: {
                option: css.loyalty__option,
                option__name: css.loyalty__option_name,
                option__description: css.loyalty__option_description
            }
        }
    }), []);
    const Content = props => {
        const { LoyaltySummaryV2: LoyaltySummaryV2Override = LoyaltySummary } = useRenderers();
        const options = travellersWithAvailableLoyalty.map(traveller => {
            var _a;
            const card = getUserValue(traveller, TravellerFieldEnum.LoyaltyNumber);
            const points = (_a = ffpBonusesForFlightByTravellers[traveller.id]) === null || _a === void 0 ? void 0 : _a.bonusForFlight;
            const items = [
                {
                    id: traveller.id,
                    name: card || t('No card'),
                    included: !!card,
                    icon: CardIcon
                }
            ];
            if (points) {
                items.push({
                    id: traveller.id,
                    name: t('points to be credited'),
                    included: false,
                    icon: TransparentStarIcon,
                    description: String(points)
                });
            }
            return {
                header: getTravellerName(traveller),
                items
            };
        });
        return (React.createElement(React.Fragment, null,
            !milesBonus && (React.createElement("p", { className: css.loyalty__notAwardedText }, t('Start saving now! Earn miles and spend them on the next flights.'))),
            React.createElement(ThemeProvider, { value: serviceSummaryOptionStyles },
                React.createElement(LoyaltySummaryV2Override, { options: options }))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(CheckoutAdditionalService, { icon: React.createElement(CdnImage, { path: `CardExtra/loyalty2.svg`, fallbackUrl: `https://cdn.websky.aero/content/frontend/images/ZZ/CardExtra/loyalty2.svg` }), className: cn(css.loyalty, { [css.loyaltyV2]: isServiceV2 }), header: t(loyaltyProgramName), headerClassName: css.header, headerTextClassName: css.header__text, headerPriceClassName: css.header__price, mainClassName: css.main, addButtonText: t('Register'), description: t('Start earning miles now to flight cheaper'), isSelected: isActive, addClick: isActive ? openLoyaltyModal : openConnectModal, buttons: buttons, buttonsClassName: css.loyalty__buttons },
            React.createElement(Content, { milesBonus: milesBonus, authState: ffpAuthState })),
        React.createElement(LinkCardsModal, { isOpen: isLoyaltyModalOpen, onClose: closeLoyaltyModal, travellers: travellersWithAvailableLoyalty, userInfo: userInfo, onOpenConnectModal: openConnectModal, onGoToAccountLoyalty: handleViewStatusClick, error: formError, isLoading: loading, onSubmit: onSubmit, onClearError: clearFormError, hasLoyaltyProgramURL: !!loyaltyProgramAccountUrl }),
        React.createElement(LoyaltyConnect, { isOpen: isConnectModalOpen, userInfo: user, showRegisterAction: true, authType: 'authenticate', onLoyaltyCardSaved: onLoyaltyCardSaved, onClose: closeConnectModal, onGoToSettings: onGoToAccountSettings })));
};
export default Loyalty;
